import { io, Socket } from 'socket.io-client';
import config from '@root/src/config';

const WS_RECONNECTION_ATTEMPTS = 5;
const WS_RECONNECTION_DELAY_MS = 1000;
const WS_RECONNECTION_DELAY_MAX_MS = 5000;

export interface ConversationMessageNewEvent {
  conversationId: string;
  message: {
    id: string;
    author: { id: string; firstName: string; lastName: string };
    content: string;
    createdAt: string;
    edited: boolean;
  };
}

export interface ConversationMessageUpdatedEvent {
  conversationId: string;
  message: {
    id: string;
    content: string;
    updatedAt: string;
    edited: boolean;
  };
}

export interface ConversationMessageDeletedEvent {
  conversationId: string;
  messageId: string;
}

export interface ConversationReadEvent {
  conversationId: string;
  userId: string;
  readAt: string;
}

export interface ConversationTypingUpdateEvent {
  conversationId: string;
  userId: string;
  isTyping: boolean;
}

export interface ForumMessageNewEvent {
  forumId: string;
  message: {
    id: string;
    author: { id: string; firstName: string; lastName: string };
    content: string;
    parentMessageId: string | null;
    sstBlockId: string | null;
    createdAt: string;
    edited: boolean;
  };
}

export interface ForumReplyNewEvent {
  forumId: string;
  parentMessageId: string;
  message: {
    id: string;
    author: { id: string; firstName: string; lastName: string };
    content: string;
    createdAt: string;
  };
}

export interface ForumMessageUpdatedEvent {
  forumId: string;
  message: {
    id: string;
    content: string;
    updatedAt: string;
    edited: boolean;
  };
}

export interface ForumMessageDeletedEvent {
  forumId: string;
  messageId: string;
}

export interface ForumParticipantJoinedEvent {
  forumId: string;
  user: { id: string; firstName: string; lastName: string };
  joinedAt: string;
}

export interface ForumParticipantLeftEvent {
  forumId: string;
  userId: string;
  leftAt: string;
}

export interface ForumParticipantRoleChangedEvent {
  forumId: string;
  userId: string;
  oldRole: string;
  newRole: string;
  changedAt: string;
}

export interface ForumFileUploadedEvent {
  forumId: string;
  file: {
    id: string;
    filename: string;
    mimeType: string;
    fileSize: number;
    messageId: string;
    uploadedBy: { id: string; firstName: string; lastName: string };
    createdAt: string;
  };
}

export interface SstCommentNewEvent {
  sstId: string;
  comment: {
    id: string;
    comment: string;
    createdBy: {
      userId: string;
      firstName: string;
      lastName: string;
      memberId: string | null;
    };
    timestamp: string;
  };
}

export interface SstCommentUpdatedEvent {
  sstId: string;
  comment: { id: string; comment: string; timestamp: string };
}

export interface SstCommentDeletedEvent {
  sstId: string;
  commentId: string;
}

type EventCallback<T = unknown> = (data: T) => void;

class RealtimeManager {
  private socket: Socket | null = null;
  /**
   * Room key -> number of consumers holding it. The backend keeps one membership per
   * socket, not a count: a second `socket.join` is a no-op and a single `socket.leave`
   * drops it (backend src/websocket/handlers/forums.js:58,71, conversations.js:23,36,
   * ssts.js:29,42; socket.io-adapter in-memory-adapter.js:46,68 stores rooms in a Set).
   * So the count lives here: join is emitted on 0 -> 1, leave on 1 -> 0.
   */
  private roomRefCounts = new Map<string, number>();
  private eventListeners = new Map<string, EventCallback[]>();
  private connectionPromise: Promise<Socket> | null = null;
  private connectionStateListeners = new Set<(connected: boolean) => void>();

  private async getAuthToken(): Promise<string | null> {
    try {
      const response = await fetch('/api/auth/websocket-token', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = (await response.json()) as { token?: string };
        return data.token ?? null;
      }
      return null;
    } catch {
      return null;
    }
  }

  async connect(): Promise<Socket> {
    if (this.socket?.connected) return this.socket;
    if (this.connectionPromise) return this.connectionPromise;

    this.connectionPromise = this.establishConnection().catch(err => {
      this.connectionPromise = null;
      throw err;
    });
    return this.connectionPromise;
  }

  private async establishConnection(): Promise<Socket> {
    const token = await this.getAuthToken();
    if (!token) throw new Error('WebSocket: no auth token available');

    this.socket = io(config.url.urlWebSocket, {
      path: '/back/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: WS_RECONNECTION_ATTEMPTS,
      reconnectionDelay: WS_RECONNECTION_DELAY_MS,
      reconnectionDelayMax: WS_RECONNECTION_DELAY_MAX_MS,
      withCredentials: true,
      auth: { token },
    });

    // Forward every server event into the internal bus — survives reconnects
    this.socket.onAny((event: string, data: unknown) => {
      this.emitToListeners(event, data);
    });

    // Persistent: runs on first connect AND every auto-reconnect
    this.socket.on('connect', () => {
      this.connectionPromise = null;
      this.rejoinRooms();
      this.notifyConnectionStateListeners(true);
    });

    this.socket.on('connect_error', error => {
      console.error('WebSocket connection error:', error.message);
    });

    this.socket.on('disconnect', reason => {
      console.log('WebSocket disconnected:', reason);
      this.connectionPromise = null;
      this.notifyConnectionStateListeners(false);
    });

    return new Promise((resolve, reject) => {
      this.socket!.once('connect', () => resolve(this.socket!));
      this.socket!.once('reconnect_failed', () => {
        this.connectionPromise = null;
        reject(new Error('WebSocket: failed to connect after maximum retries'));
      });
    });
  }

  private rejoinRooms(): void {
    if (!this.socket?.connected) return;
    this.roomRefCounts.forEach((_count, roomKey) => {
      const [roomType, roomId] = roomKey.split(':');
      this.socket!.emit(`${roomType}:join`, { [`${roomType}Id`]: roomId }, (res: { ok: boolean; error?: string }) => {
        if (!res.ok) console.error(`WebSocket: failed to rejoin ${roomKey}:`, res.error);
      });
    });
  }

  joinRoom(roomType: 'conversation' | 'forum' | 'sst', roomId: string): void {
    const roomKey = `${roomType}:${roomId}`;
    const count = this.roomRefCounts.get(roomKey) ?? 0;
    this.roomRefCounts.set(roomKey, count + 1);

    if (count === 0 && this.socket?.connected) {
      this.socket.emit(`${roomType}:join`, { [`${roomType}Id`]: roomId }, (res: { ok: boolean; error?: string }) => {
        if (!res.ok) console.error(`WebSocket: failed to join ${roomKey}:`, res.error);
      });
    }
  }

  leaveRoom(roomType: 'conversation' | 'forum' | 'sst', roomId: string): void {
    const roomKey = `${roomType}:${roomId}`;
    const count = this.roomRefCounts.get(roomKey) ?? 0;
    if (count === 0) return;

    if (count > 1) {
      this.roomRefCounts.set(roomKey, count - 1);
      return;
    }

    this.roomRefCounts.delete(roomKey);
    if (this.socket?.connected) {
      this.socket.emit(`${roomType}:leave`, { [`${roomType}Id`]: roomId }, (res: { ok: boolean; error?: string }) => {
        if (!res.ok) console.error(`WebSocket: failed to leave ${roomKey}:`, res.error);
      });
    }
  }

  // Returns a cleanup function — call it to unsubscribe
  on<T = unknown>(event: string, callback: EventCallback<T>): () => void {
    const cb = callback as EventCallback;
    const listeners = this.eventListeners.get(event) ?? [];
    listeners.push(cb);
    this.eventListeners.set(event, listeners);
    return () => this.off(event, cb);
  }

  off(event: string, callback: EventCallback): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners) return;
    const index = listeners.indexOf(callback);
    if (index > -1) listeners.splice(index, 1);
  }

  private emitToListeners(event: string, data: unknown): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners) return;
    listeners.forEach(cb => {
      try {
        cb(data);
      } catch (error) {
        console.error(`WebSocket: error in listener for "${event}":`, error);
      }
    });
  }

  startTyping(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('conversation:typing:start', { conversationId });
    }
  }

  stopTyping(conversationId: string): void {
    if (this.socket?.connected) {
      this.socket.emit('conversation:typing:stop', { conversationId });
    }
  }

  onConnectionStateChange(listener: (connected: boolean) => void): () => void {
    this.connectionStateListeners.add(listener);
    return () => this.connectionStateListeners.delete(listener);
  }

  private notifyConnectionStateListeners(connected: boolean): void {
    this.connectionStateListeners.forEach(listener => listener(connected));
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.roomRefCounts.clear();
    this.connectionPromise = null;
    // Note: eventListeners and connectionStateListeners are intentionally kept —
    // they are managed by consumers via the cleanup functions returned from on()
    // and onConnectionStateChange(). Clearing them here would permanently break
    // subscriptions in still-mounted components after a reconnect.
  }
}

// Singleton instance
export const realtimeManager = new RealtimeManager();
